export const reconciliationWeightEventTypeValues = [
  'holdingUnitWeightAdded',
  'holdingUnitWeightCorrected',
  'holdingUnitAdjustmentApplied',
] as const;

export type ReconciliationWeightEventType = (typeof reconciliationWeightEventTypeValues)[number];

export const reconciliationSnapshotTypeValues = ['daily_intake', 'total_inventory'] as const;
export type ReconciliationSnapshotType = (typeof reconciliationSnapshotTypeValues)[number];

export const totalInventoryBreakdownStatusValues = ['full_breakdown'] as const;
export type TotalInventoryBreakdownStatus = (typeof totalInventoryBreakdownStatusValues)[number];

export const totalInventoryOutboundSourceTypeValues = [
  'shipment_untracked',
  'shipment_non_inbound',
  'shipment_inbound_receipt',
  'cover_shipment_own_purchase',
  'cover_shipment_inbound_receipt',
] as const;
export type TotalInventoryOutboundSourceType =
  (typeof totalInventoryOutboundSourceTypeValues)[number];

export const dailyIntakeSettlementStatusValues = ['settled', 'pending_settlement'] as const;
export type DailyIntakeSettlementStatus = (typeof dailyIntakeSettlementStatusValues)[number];

export const dailyIntakeWarningCodeValues = ['pending_settlement'] as const;
export type DailyIntakeWarningCode = (typeof dailyIntakeWarningCodeValues)[number];

export const dailyIntakeManualPhysicalContributionKindValues = ['intake', 'reversal'] as const;
export type DailyIntakeManualPhysicalContributionKind =
  (typeof dailyIntakeManualPhysicalContributionKindValues)[number];

export const dailyIntakeManualPhysicalContributionSourceEventTypeValues = [
  'holdingUnitWeightAdded',
  'holdingUnitInventoryImpactReversed',
] as const;
export type DailyIntakeManualPhysicalContributionSourceEventType =
  (typeof dailyIntakeManualPhysicalContributionSourceEventTypeValues)[number];

export interface DailyIntakeScopeDto {
  storeId: string | null;
  locationId: string | null;
  stationId: string | null;
}

export interface DailyIntakeTotalsDto {
  physicalIntakeLb: number;
  systemIntakeLb: number;
  varianceLb: number;
  purchaseContributionCount: number;
  inboundContributionCount: number;
  manualPhysicalContributionCount: number;
}

export interface DailyIntakeMaterialRowDto {
  materialId: string | null;
  physicalIntakeLb: number;
  systemIntakeLb: number;
  varianceLb: number;
  purchaseContributionCount: number;
  inboundContributionCount: number;
  manualPhysicalContributionCount: number;
}

export interface DailyIntakePurchaseContributionDto {
  purchaseCommittedId: string;
  lineItemId: string;
  materialId: string;
  materialDisplayName: string;
  presentationType: 'SW' | 'SCL' | 'SCS' | 'SC25' | 'SP';
  folioNumber: number;
  netWeightLb: number;
  pieces: number | null;
  equivalentWeightLb: number | null;
  systemIntakeLb: number;
  occurredAt: string;
  storeId: string;
  locationId: string;
  stationId: string;
  stationLabel: string;
  operatorUserId: string;
}

export interface DailyIntakeInboundContributionDto {
  postingId: string;
  receiptId: string;
  materialId: string;
  createdHoldingUnitIds: string[];
  occurredAt: string;
  storeId: string;
  locationId: string;
  stationId: string;
  postedByUserId: string | null;
  physicalIntakeLb: number;
  systemIntakeLb: number;
  officialPaymentWeightLb?: number;
  settlementStatus: DailyIntakeSettlementStatus;
}

export interface DailyIntakeManualPhysicalContributionDto {
  eventId: string;
  unitId: string;
  materialId: string | null;
  contributionKind: DailyIntakeManualPhysicalContributionKind;
  sourceEventType: DailyIntakeManualPhysicalContributionSourceEventType;
  reversesEventId?: string;
  occurredAt: string;
  storeId: string;
  locationId: string;
  stationId: string;
  actorUserId: string;
  physicalIntakeLb: number;
}

export interface DailyIntakeWarningDto {
  code: DailyIntakeWarningCode;
  message: string;
  postingId?: string;
  receiptId?: string;
  materialId?: string | null;
}

export interface GetDailyIntakeReviewRequest {
  companyId: string;
  rangeStartAt: string;
  rangeEndAt: string;
  storeId?: string;
  locationId?: string;
  stationId?: string;
}

export interface GetDailyIntakeReviewResponse {
  generatedAt: string;
  rangeStartAt: string;
  rangeEndAt: string;
  scope: DailyIntakeScopeDto;
  totals: DailyIntakeTotalsDto;
  materials: DailyIntakeMaterialRowDto[];
  purchaseContributions: DailyIntakePurchaseContributionDto[];
  inboundContributions: DailyIntakeInboundContributionDto[];
  manualPhysicalContributions: DailyIntakeManualPhysicalContributionDto[];
  warnings: DailyIntakeWarningDto[];
}

export interface DailyIntakeSnapshotRowDto {
  materialId: string | null;
  physicalIntakeLb: number;
  systemIntakeLb: number;
  varianceLb: number;
  purchaseContributionCount: number;
  inboundContributionCount: number;
  manualPhysicalContributionCount: number;
}

export interface TotalInventorySnapshotRowDto {
  materialId: string | null;
  physicalOnHandLb: number;
  systemCrvWeightLb: number | null;
  systemScrapWeightLb: number | null;
  systemTotalWeightLb: number;
  varianceLb: number;
}

interface ReconciliationSnapshotBaseDto {
  id: string;
  type: ReconciliationSnapshotType;
  companyId: string;
  storeId: string;
  locationId: string;
  stationId: string;
  timeZone: string | null;
  notes: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

export interface DailyIntakeSnapshotSummaryDto extends ReconciliationSnapshotBaseDto {
  type: 'daily_intake';
  generatedAt: string;
  rangeStartAt: string;
  rangeEndAt: string;
  scope: DailyIntakeScopeDto;
  physicalIntakeLb: number;
  systemIntakeLb: number;
  varianceLb: number;
}

export interface TotalInventorySnapshotSummaryDto extends ReconciliationSnapshotBaseDto {
  type: 'total_inventory';
  asOf: string;
  breakdownStatus: TotalInventoryBreakdownStatus;
  physicalOnHandLb: number;
  systemCrvWeightLb: number | null;
  systemScrapWeightLb: number | null;
  systemTotalWeightLb: number;
  varianceLb: number;
}

export type ReconciliationSnapshotSummaryDto =
  | DailyIntakeSnapshotSummaryDto
  | TotalInventorySnapshotSummaryDto;

export interface DailyIntakeSnapshotDetailDto extends DailyIntakeSnapshotSummaryDto {
  totals: DailyIntakeTotalsDto;
  rows: DailyIntakeSnapshotRowDto[];
  purchaseContributions: DailyIntakePurchaseContributionDto[];
  inboundContributions: DailyIntakeInboundContributionDto[];
  manualPhysicalContributions: DailyIntakeManualPhysicalContributionDto[];
  warnings: DailyIntakeWarningDto[];
}

export interface TotalInventorySnapshotDetailDto extends TotalInventorySnapshotSummaryDto {
  rows: TotalInventorySnapshotRowDto[];
}

export type ReconciliationSnapshotDetailDto =
  | DailyIntakeSnapshotDetailDto
  | TotalInventorySnapshotDetailDto;

export interface CreateDailyIntakeSnapshotRequest {
  companyId: string;
  rangeStartAt: string;
  rangeEndAt: string;
  storeId?: string;
  locationId?: string;
  stationId?: string;
  timeZone?: string;
  notes?: string;
}

export interface CreateDailyIntakeSnapshotResponse {
  snapshot: DailyIntakeSnapshotDetailDto;
}

export interface CreateTotalInventorySnapshotRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  stationId?: string;
  asOf?: string;
  timeZone?: string;
  notes?: string;
}

export interface CreateTotalInventorySnapshotResponse {
  snapshot: TotalInventorySnapshotDetailDto;
}

export interface ListReconciliationSnapshotsRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  type?: ReconciliationSnapshotType;
  from?: string;
  to?: string;
  limit?: number;
}

export interface ListReconciliationSnapshotsResponse {
  items: ReconciliationSnapshotSummaryDto[];
}

export interface GetReconciliationSnapshotDetailRequest {
  companyId: string;
  snapshotId: string;
}

export interface GetReconciliationSnapshotDetailResponse {
  snapshot: ReconciliationSnapshotDetailDto;
}

export interface TotalInventoryRowDto {
  storeId: string;
  locationId: string;
  materialId: string | null;
  physicalOnHandLb: number;
  systemCrvWeightLb: number;
  systemScrapWeightLb: number;
  systemTotalWeightLb: number;
  inboundPostedCrvWeightLb: number;
  inboundPostedScrapWeightLb: number;
  inboundPostedTotalWeightLb: number;
  purchasedCrvWeightLb: number;
  purchasedScrapWeightLb: number;
  purchasedTotalWeightLb: number;
  outboundCrvWeightLb: number;
  outboundScrapWeightLb: number;
  outboundTotalWeightLb: number;
  varianceLb: number;
  physicalUnitCount: number;
}

export interface TotalInventoryTotalsDto {
  physicalOnHandLb: number;
  systemCrvWeightLb: number;
  systemScrapWeightLb: number;
  systemTotalWeightLb: number;
  inboundPostedCrvWeightLb: number;
  inboundPostedScrapWeightLb: number;
  inboundPostedTotalWeightLb: number;
  purchasedCrvWeightLb: number;
  purchasedScrapWeightLb: number;
  purchasedTotalWeightLb: number;
  outboundCrvWeightLb: number;
  outboundScrapWeightLb: number;
  outboundTotalWeightLb: number;
  varianceLb: number;
  physicalUnitCount: number;
}

export interface GetTotalInventoryReconciliationRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  materialId?: string;
  asOf?: string;
}

export interface GetTotalInventoryReconciliationResponse {
  generatedAt: string;
  asOf: string;
  storeId: string | null;
  locationId: string | null;
  materialId: string | null;
  totals: TotalInventoryTotalsDto;
  rows: TotalInventoryRowDto[];
}

export interface TotalInventoryPhysicalUnitDetailDto {
  unitId: string;
  lifecycleStatus: 'open' | 'ready';
  materialId: string | null;
  netLb: number;
  yardLocationId: string | null;
  updatedAt: string;
}

export interface TotalInventoryInboundPostingContributionDto {
  postingId: string;
  receiptId: string;
  materialId: string;
  createdHoldingUnitId: string;
  isCrvEligible: boolean;
  postedWeightLb: number;
  crvWeightLb: number;
  scrapWeightLb: number;
  totalWeightLb: number;
  occurredAt: string;
  storeId: string;
  locationId: string;
  stationId: string;
  postedByUserId: string | null;
}

export interface TotalInventoryPurchaseContributionDto {
  purchaseCommittedId: string;
  lineItemId: string;
  materialId: string;
  materialDisplayName: string;
  presentationType: 'SW' | 'SCL' | 'SCS' | 'SC25' | 'SP';
  pieces: number | null;
  equivalentWeightLb: number | null;
  isCrvEligible: boolean;
  netWeightLb: number;
  crvWeightLb: number;
  scrapWeightLb: number;
  totalWeightLb: number;
  occurredAt: string;
  storeId: string;
  locationId: string;
  stationId: string;
  operatorUserId: string;
}

export interface TotalInventoryOutboundContributionDto {
  sourceType: TotalInventoryOutboundSourceType;
  sourceId: string;
  materialId: string;
  inventoryOutEffectiveAt: string;
  storeId: string;
  locationId: string;
  stationId: string;
  crvWeightLb: number;
  scrapWeightLb: number;
  totalWeightLb: number;
  purchaseLineCount: number;
}

export interface GetTotalInventoryReconciliationDetailRequest {
  companyId: string;
  locationId: string;
  materialId?: string | null;
  asOf?: string;
  limit?: number;
}

export interface GetTotalInventoryReconciliationDetailResponse {
  generatedAt: string;
  asOf: string;
  locationId: string;
  materialId: string | null;
  totals: TotalInventoryRowDto;
  detailLimit: number;
  hasMorePhysicalUnits: boolean;
  hasMoreInboundPostingContributions: boolean;
  hasMorePurchaseContributions: boolean;
  hasMoreOutboundContributions: boolean;
  physicalUnits: TotalInventoryPhysicalUnitDetailDto[];
  inboundPostingContributions: TotalInventoryInboundPostingContributionDto[];
  purchaseContributions: TotalInventoryPurchaseContributionDto[];
  outboundContributions: TotalInventoryOutboundContributionDto[];
}

export const reconciliationAdjustmentTypeValues = ['shipment_void'] as const;
export type ReconciliationAdjustmentType = (typeof reconciliationAdjustmentTypeValues)[number];

export interface ReconciliationAdjustmentTimelineItemDto {
  id: string;
  type: ReconciliationAdjustmentType;
  occurredAt: string;
  storeId: string;
  locationId: string;
  shipmentId: string | null;
  materialId: string | null;
  summary: string;
  details: Record<string, unknown> | null;
}

export interface ListReconciliationAdjustmentsRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export interface ListReconciliationAdjustmentsResponse {
  items: ReconciliationAdjustmentTimelineItemDto[];
}
